import { useEffect, useMemo, useRef } from "react";
import { Money } from "../../../../../model/common/money";
import { Controller } from "react-hook-form";
import AdminDynamicForm from "../admin-dynamic-form/AdminDynamicForm";
import Select from "../../../../../common/components/input-service-request/Select";
import { DynamicFieldItem } from "../../../../../model/dynamic-forms";
import { Attribute } from "../../../../../model/service-request/attribute";
import { SectionDefinition } from "../../../../../model/service-request/section-definition";
import { useLazyGetServiceRequestQuery, useLazyGetServiceRequestsQuery } from "../../../../../service/customersupport-service";
import { isAgentRenderableAttribute, useServiceRequestForm } from "../../context/ServiceRequestFormContext";
import { StepProps } from "../../informations";
import styles from "./steps.module.scss";

const RequestTypeStep = ({ title, message, setDisabled }: StepProps) => {
    const { form: { control, watch, setValue, trigger, formState: { errors } }, setSectionDefinitions, forceValidateSections, setForceValidateSections } = useServiceRequestForm();
    const requestTypeId = watch("requestTypeId");
    const sections_data = watch("sections");

    const firstName = watch("firstName");
    const lastName = watch("lastName");
    const email = watch("email");
    const phone = watch("phone");
    const meterNumber = watch("meterNumber");
    const meterType = watch("meterType");
    const requestTypeName = watch("requestTypeName");

    const isEkedcIssueTracker = requestTypeName === "EKEDC Issue Tracker";

    const [fetchServiceRequests, { data, isLoading }] = useLazyGetServiceRequestsQuery();
    const [fetchServiceRequestDefinition, { data: definition }] = useLazyGetServiceRequestQuery();
    const prevRequestTypeIdRef = useRef<string | undefined>(undefined);

    const customerAutoValues: Record<string, string> = {
        "first name": firstName,
        "last name": lastName,
        "email": email,
        "email address": email,
        "meter number": meterNumber,
        "meter no": meterNumber,
        "customer name": [firstName, lastName].filter(Boolean).join(" "),
        "telephone number": phone,
        "phone number": phone,
    };

    // Resolves the Metered option mirroring what the backend stamps from the
    // meter type: prepaid -> "Yes", postpaid -> "No".
    const getMeteredOptionValue = (attr: Attribute): string | null => {
        const desired = meterType === "prepaid" ? "yes" : "no";
        const option = attr.childrenOptionDefinitions?.find((o) => o.value?.toLowerCase() === desired);
        return option ? (option.id ?? option.value) : null;
    };

    const getAutoPopulatedFieldIds = (attributeDefinitions: SectionDefinition["attributeDefinitions"]): Set<string> => {
        if (!isEkedcIssueTracker) return new Set();
        const ids = new Set(
            attributeDefinitions
                .filter((attr) => attr.label && customerAutoValues[attr.label.toLowerCase()] !== undefined)
                .map((attr) => attr.id)
        );
        // Metered is read-only only for prepaid customers
        if (meterType === "prepaid") {
            const meteredAttr = attributeDefinitions.find((attr) => attr.label?.toLowerCase() === "metered");
            if (meteredAttr) ids.add(meteredAttr.id);
        }
        return ids;
    };

    const buildPreselectedValues = (sectionId: string, attributeDefinitions: SectionDefinition["attributeDefinitions"]): DynamicFieldItem[] => {
        const stored = sections_data?.[sectionId] ?? [];
        if (!isEkedcIssueTracker) return stored;
        const autoPopulated: DynamicFieldItem[] = attributeDefinitions
            .filter((attr) => attr.label && customerAutoValues[attr.label.toLowerCase()] !== undefined)
            .map((attr) => ({
                fieldId: attr.id,
                fieldLabel: attr.label,
                fieldValue: customerAutoValues[attr.label.toLowerCase()],
                fieldType: attr.fieldType!,
                fieldPath: `${attr.id}.value`,
            }));

        // Inject Metered from the meter type (prepaid -> Yes, postpaid -> No),
        // mirroring the value the backend stamps server-side.
        const meteredAttr = attributeDefinitions.find((attr) => attr.label?.toLowerCase() === "metered");
        if (meteredAttr) {
            const meteredValue = getMeteredOptionValue(meteredAttr);
            if (meteredValue !== null) {
                autoPopulated.push({
                    fieldId: meteredAttr.id,
                    fieldLabel: meteredAttr.label,
                    fieldValue: meteredValue,
                    fieldType: meteredAttr.fieldType!,
                    fieldPath: `${meteredAttr.id}.value`,
                });
            }
        }

        return [...autoPopulated, ...stored];
    };

    useEffect(() => {
        fetchServiceRequests({ page: 1, pageSize: 100 });
    }, [fetchServiceRequests]);

    useEffect(() => {
        if (requestTypeId) {
            if (prevRequestTypeIdRef.current !== undefined && prevRequestTypeIdRef.current !== requestTypeId) {
                setValue("sections", {});
            }
            prevRequestTypeIdRef.current = requestTypeId;
            fetchServiceRequestDefinition(requestTypeId);
        }
    }, [requestTypeId, fetchServiceRequestDefinition]);

    const options = (data?.items ?? [])
        .filter((type) => type.isPublished)
        .map((type) => ({
            value: type.id,
            label: type.name,
        }));

    const sections = useMemo(
        () =>
            (definition?.sectionDefinitions ?? []).filter(
                // Skip sections with no renderable fields (all disabled, e.g.
                // "Address Information", "Additional Information") — they'd
                // render as bare titles and stray dividers. Hidden-but-enabled
                // fields (Account No, Metered) still render, read-only.
                (s) => !s.isDeleted && s.attributeDefinitions.some(isAgentRenderableAttribute)
            ),
        [definition]
    );

    useEffect(() => {
        setValue("requestTypeDefinitionId", definition?.requestTypeId || definition?.id || "");
        setValue("amount", definition?.price ? new Money(definition.price.currency, definition.price.amount) : null);
        setValue("payOnSubmit", definition?.payOnSubmit ?? null);
        setValue("paymentMethod", (definition?.price?.amount ?? 0) > 0 && definition?.payOnSubmit !== false ? 'pay_now' : null);
        if (setDisabled) {
            setDisabled(definition?.price == null || definition.price.amount === 0 || definition.payOnSubmit === false);
        }

    }, [definition]);

    useEffect(() => {
        setForceValidateSections(false);
        setSectionDefinitions(sections);
    }, [sections]);

    const handleSectionChanged = (sectionId: string, fieldValues: DynamicFieldItem[]) => {
        const updatedSections = { ...sections_data, [sectionId]: fieldValues };
        setValue("sections", updatedSections);

        let sectionPricesTotal = 0;
        let sectionCurrency: string | undefined;

        sections.forEach(section => {
            const fields = updatedSections[section.id!] ?? [];
            section.attributeDefinitions.forEach(attr => {
                if (!attr.childrenOptionDefinitions) return;
                const field = fields.find(f => f.fieldId === attr.id);
                if (!field) return;
                const selectedOption = attr.childrenOptionDefinitions.find(opt => opt.id === field.fieldValue);
                if (selectedOption?.price?.amount) {
                    sectionPricesTotal += selectedOption.price.amount;
                    sectionCurrency ??= selectedOption.price.currency;
                }
            });
        });

        const baseAmount = definition?.price?.amount ?? 0;
        const totalAmount = baseAmount + sectionPricesTotal;
        const currency = definition?.price?.currency ?? sectionCurrency ?? "";

        setValue("amount", totalAmount > 0 ? new Money(currency, totalAmount) : null);
        setValue("paymentMethod", totalAmount > 0 && definition?.payOnSubmit !== false ? "pay_now" : null);
        if (setDisabled) setDisabled(totalAmount === 0 || definition?.payOnSubmit === false);
    };

    return (
        <div className={styles.mainStepContent}>
            <div className={styles.headerContainer}>
                <h2 className={styles.headerTitle}>{title}</h2>
                <p className={styles.headerDescription}>{message}</p>
            </div>

            <div className={styles.formContainer}>
                <div className={styles.formFieldContainer}>
                    <Controller
                        name="requestTypeId"
                        control={control}
                        rules={{ required: "Please select a request type" }}
                        render={({ field }) => (
                            <Select
                                label="Request Type"
                                options={options}
                                value={field.value}
                                disabled={isLoading}
                                error={errors.requestTypeId?.message}
                                onChange={(opt) => {
                                    field.onChange(opt.value);
                                    setValue("requestTypeName", opt.label);
                                    trigger("requestTypeId");
                                }}
                            />
                        )}
                    />
                </div>

                {sections.length > 0 && (
                    <>
                        <hr className={styles.divider} />
                        {sections.map((section, index) => (
                            <div key={section.id}>
                                <AdminDynamicForm
                                    sectionDefinition={section}
                                    forceValidate={forceValidateSections}
                                    preselectedFormValues={buildPreselectedValues(section.id!, section.attributeDefinitions)}
                                    readOnlyFieldIds={getAutoPopulatedFieldIds(section.attributeDefinitions)}
                                    formStateChanged={(fieldValues) => handleSectionChanged(section.id!, fieldValues)}
                                />
                                {index < sections.length - 1 && <hr className={styles.divider} />}
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

export default RequestTypeStep;
